'use client'

import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import S from './TasksFeedGrid.module.scss'

const SKELETON_CARDS = 8

export type TasksFeedGridProps = {
  children: ReactNode
  /** Rendered instead of the grid when there are no cards to show. */
  empty?: ReactNode
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
}

export default function TasksFeedGrid({
  children,
  empty,
  hasMore,
  isLoadingMore,
  onLoadMore
}: TasksFeedGridProps) {
  const t = useTranslations('tasksPage.taskCard')

  return (
    <section className={S.root} aria-label={t('listAriaLabel')}>
      {empty ?? <div className={S.grid}>{children}</div>}
      {hasMore ? (
        <button
          type="button"
          className={S.loadMore}
          disabled={isLoadingMore}
          aria-busy={isLoadingMore}
          onClick={onLoadMore}
        >
          {t('loadMoreCta')}
        </button>
      ) : null}
    </section>
  )
}

export function TasksFeedGridLoading() {
  const t = useTranslations('tasksPage.taskCard')

  return (
    <div className={S.root} role="status" aria-busy="true">
      <span className={S.srOnly}>{t('listLoading')}</span>
      <div className={S.grid} aria-hidden>
        {Array.from({ length: SKELETON_CARDS }, (_, index) => (
          <div key={index} className={S.skeletonCard}>
            <div className={S.skeletonCover} />
            <div className={S.skeletonBody}>
              <span className={S.skeletonLineShort} />
              <span className={S.skeletonLine} />
              <span className={S.skeletonLineShort} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function TasksFeedGridError({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations('tasksPage.taskCard')

  return (
    <div className={S.root}>
      <div className={S.error} role="alert">
        <p className={S.errorText}>{t('listLoadError')}</p>
        <button type="button" className={S.loadMore} onClick={onRetry}>
          {t('listRetry')}
        </button>
      </div>
    </div>
  )
}
